import { useState } from "react";
import {
  faEdit,
  faCheck,
  faXmark,
  faTrashAlt
} from "@fortawesome/free-solid-svg-icons";
import { Col, Collapse } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useParams, useOutletContext } from "react-router";
import {
  updateCompanyNameForAgent,
  updateProductNameForAgent,
  updateBudgetQueriesForAgent,
  updatePricesPerAreaForAgent,
  updatePricePerInvoiceUnitForAgent,
  updateNameOfAccountingUniForAgent,
  updateBudgetAreaOfQueryBoundsForAgent,
  updateFixedPricePerInvoiceUnitForAgent,
  updateLeadServicePlusPriceTypeForAgent,
  updateLeadserviceIndividualNoteForAgent
} from "../../../services/api-declaration";
import { useDialog } from "../../../hooks/useDialog";
import LeadServiceActionButton from "./lead-service-action-button";
import { LEAD_SERVICE_PLUS_EDIT_MODES, PRICE_TYPE } from "../../strings";
import { showError, showSuccess } from "../../../services/toast-service";
import { useTranslation } from "react-i18next";

const LeadServiceActionsSection = ({
  editMode,
  actionsFor,
  isBtnDisabled,
  handleEditMode,
  hideDelete = false
}) => {
  const id = useParams().id;
  const { t } = useTranslation();
  const { funnel, setFunnel } = useOutletContext();
  const {
    FOR_PRICE,
    FOR_FIXED_PRICE,
    FOR_COMPANY,
    FOR_PRODUCT,
    FOR_UNIT,
    FOR_BUDGET_QUERY,
    FOR_PRICES_PER_AREA,
    FOR_BUDGET_QUERY_BOUNDS,
    FOR_INDIVIDUAL_NOTE
  } = LEAD_SERVICE_PLUS_EDIT_MODES;
  const { reset, watch, getValues, handleSubmit } = useFormContext();
  const [initialPrice, setInitialPrice] = useState(0);
  const [initialFixedPrice, setFixedInitialPrice] = useState(0);
  const [initialUnit, setInitialUnit] = useState("");
  const [initialMinBudgetQuery, setInitialMinBudgetQuery] = useState(0);
  const [initialMaxBudgetQuery, setInitialMaxBudgetQuery] = useState(0);
  const [initialPriceBelowMinBudget, setInitialPriceBelowMinBudget] =
    useState(0);
  const [initialPriceBetweenBudget, setInitialPriceBetweenBudget] = useState(0);
  const [initialPriceAboveMaxBudget, setInitialPriceAboveMaxBudget] =
    useState(0);
  const [initialCompanyName, setInitialCompanyName] = useState("");
  const [initialProductName, setInitialProductName] = useState("");
  const [initialBudgetAreaOfQueryBounds, setInitialBudgetAreaOfQueryBounds] =
    useState(0);
  const [initialIndividualNote, setInitialIndividualNote] = useState(0);

  const [isSubmittingCompany, setIsSubmittingCompany] = useState({
    forAdd: false,
    forReset: false
  });
  const [isSubmittingProduct, setIsSubmittingProduct] = useState({
    forAdd: false,
    forReset: false
  });
  const [isSubmittingPrice, setIsSubmittingPrice] = useState(false);
  const [isSubmittingFixedPrice, setIsSubmittingFixedPrice] = useState(false);
  const [isSubmittingBudgetQueryLimits, setIsSubmittingBudgetQueryLimits] =
    useState(false);
  const [isSubmittingPricesPerArea, setIsSubmittingPricesPerArea] =
    useState(false);
  const [isSubmittingUnit, setIsSubmittingUnit] = useState({
    forAdd: false,
    forReset: false
  });
  const [
    isSubmittingBudgetAreaOfQueryBounds,
    setIsSubmittingBudgetAreaOfQueryBounds
  ] = useState(false);
  const [isSubmittingIndividualNote, setIsSubmittingIndividualNote] = useState({
    forAdd: false,
    forReset: false
  });

  const { showConfirmationDialogBox } = useDialog();

  const afterSubmit = ({ msg, values, forReset, field: { prop, val } }) => {
    showSuccess(msg);
    setFunnel({
      ...funnel,
      [prop]: val
    });
    reset({ ...values, [prop]: val }, { keepDirtyValues: !forReset });
    handleEditMode({ ...editMode, [actionsFor]: false });
  };
  const onError = (error) => {
    showError(error.response?.data?.message || t("leadServiceError"));
  };
  const getPositiveNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0
      ? numericValue
      : null;
  };
  const showInvalidValueError = () => {
    showError(t("common.invalidPriceValue"));
  };
  const updateSavedValues = (values, savedValues) => {
    setFunnel({
      ...funnel,
      ...savedValues
    });
    reset({ ...values, ...savedValues });
  };
  const onSubmitCompanyName = async (values, { forReset }) => {
    try {
      !forReset
        ? setIsSubmittingCompany({ ...isSubmittingCompany, forAdd: true })
        : setIsSubmittingCompany({ ...isSubmittingCompany, forReset: true });
      const companyName = forReset ? "" : values.companyNameForLeadAgent;
      const response = await updateCompanyNameForAgent(id, {
        companyNameForLeadAgent: companyName
      });
      afterSubmit({
        values,
        forReset,
        msg: response.data.message,
        field: { prop: "companyNameForLeadAgent", val: companyName }
      });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingCompany({ forAdd: false, forReset: false });
    }
  };
  const onSubmitProductName = async (values, { forReset }) => {
    try {
      !forReset
        ? setIsSubmittingProduct({ ...isSubmittingProduct, forAdd: true })
        : setIsSubmittingProduct({ ...isSubmittingProduct, forReset: true });
      const productName = forReset ? "" : values.leadServiceProductName;
      const response = await updateProductNameForAgent(id, {
        leadServiceProductName: productName
      });
      afterSubmit({
        values,
        forReset,
        msg: response.data.message,
        field: { prop: "leadServiceProductName", val: productName }
      });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingProduct({ forAdd: false, forReset: false });
    }
  };
  const onSubmitPrice = async (values) => {
    try {
      setIsSubmittingPrice(true);
      const { pricePerInvoiceUnitForLeadAgent, leadServicePlusPriceType } =
        values;
      const price = getPositiveNumber(pricePerInvoiceUnitForLeadAgent);
      if (price === null) {
        showInvalidValueError();
        return;
      }
      const payload = {
        leadServicePlusPriceType,
        pricePerInvoiceUnitForLeadAgent: price
      };
      const response = await updatePricePerInvoiceUnitForAgent(id, payload);
      showSuccess(response.data.message);
      updateSavedValues(values, payload);
      handleEditMode({ ...editMode, [actionsFor]: false });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingPrice(false);
    }
  };
  const onSubmitFixedPrice = async (values) => {
    try {
      setIsSubmittingFixedPrice(true);
      const { fixedPricePerInvoiceUnitForLeadAgent, leadServicePlusPriceType } =
        values;
      const fixedPrice = getPositiveNumber(
        fixedPricePerInvoiceUnitForLeadAgent
      );
      if (fixedPrice === null) {
        showInvalidValueError();
        return;
      }
      const payload = {
        leadServicePlusPriceType,
        fixedPricePerInvoiceUnitForLeadAgent: fixedPrice
      };
      const response = await updateFixedPricePerInvoiceUnitForAgent(
        id,
        payload
      );
      showSuccess(response.data.message);
      updateSavedValues(values, payload);
      handleEditMode({ ...editMode, [actionsFor]: false });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingFixedPrice(false);
    }
  };
  const handleUpdatePriceTypeForAgent = async ({ value }) => {
    try {
      await updateLeadServicePlusPriceTypeForAgent(id, {
        leadServicePlusPriceType: value,
        resetAreaQueries: true
      });
      const areaQueriesData = {
        leadServicePlusPriceType: value,
        minBudgetQuery: 0,
        maxBudgetQuery: 0,
        pricePerAreaBelowMinBudget: 0,
        pricePerAreaBetweenBudget: 0,
        pricePerAreaAboveMaxBudget: 0
      };
      setFunnel({ ...funnel, ...areaQueriesData });
      reset({ ...getValues(), ...areaQueriesData });
    } catch (error) {
      onError(error);
    }
  };
  const handleUpdateAccountingUnit = async ({ values, forReset }) => {
    try {
      !forReset
        ? setIsSubmittingUnit({ ...isSubmittingUnit, forAdd: true })
        : setIsSubmittingUnit({ ...isSubmittingUnit, forReset: true });
      const unit = forReset ? "" : values.nameOfAccountingUnitForLeadAgent;
      const response = await updateNameOfAccountingUniForAgent(id, {
        nameOfAccountingUnitForLeadAgent: unit
      });
      afterSubmit({
        values,
        forReset,
        msg: response.data.message,
        field: {
          prop: "nameOfAccountingUnitForLeadAgent",
          val: unit
        }
      });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingUnit({ forAdd: false, forReset: false });
    }
  };
  const onSubmitUnit = (values, { forReset }) => {
    if (
      (forReset || !values.nameOfAccountingUnitForLeadAgent) &&
      watch("leadServicePlusPriceType") === PRICE_TYPE.AREA_QUERY
    ) {
      // show dialog for confirmation if deleting, resetting name of accounting unit and price type is AREA_QUERY
      showConfirmationDialogBox({
        title: "Warning",
        dialogType: "warning",
        dialogMessage: renderConfirmationMsg(),
        responseAction: (action) => {
          if (action) {
            handleUpdateAccountingUnit({ values, forReset });
            handleUpdatePriceTypeForAgent({ value: PRICE_TYPE.DYNAMIC });
          }
        }
      });
      return;
    }
    handleUpdateAccountingUnit({ values, forReset });
  };
  const renderConfirmationMsg = () => (
    <div
      dangerouslySetInnerHTML={{
        __html: t(
          "funnelIntegration.leadServicePlus.removeUnitNameConfirmationMsg"
        )
      }}
    ></div>
  );
  const onSubmitBudgetQueryLimits = async (values) => {
    try {
      setIsSubmittingBudgetQueryLimits(true);
      const { leadServicePlusPriceType, minBudgetQuery, maxBudgetQuery } =
        values;
      const minBudget = getPositiveNumber(minBudgetQuery);
      const maxBudget = getPositiveNumber(maxBudgetQuery);
      if (minBudget === null || maxBudget === null) {
        showInvalidValueError();
        return;
      }
      const payload = {
        leadServicePlusPriceType,
        minBudgetQuery: minBudget,
        maxBudgetQuery: maxBudget
      };
      const response = await updateBudgetQueriesForAgent(id, payload);
      showSuccess(response.data.message);
      updateSavedValues(values, payload);
      handleEditMode({ ...editMode, [actionsFor]: false });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingBudgetQueryLimits(false);
    }
  };
  const onSubmitPricesPerArea = async (values) => {
    try {
      setIsSubmittingPricesPerArea(true);
      const {
        leadServicePlusPriceType,
        pricePerAreaBelowMinBudget,
        pricePerAreaBetweenBudget,
        pricePerAreaAboveMaxBudget
      } = values;
      const priceBelowMinBudget = getPositiveNumber(pricePerAreaBelowMinBudget);
      const priceBetweenBudget = getPositiveNumber(pricePerAreaBetweenBudget);
      const priceAboveMaxBudget = getPositiveNumber(pricePerAreaAboveMaxBudget);
      if (
        priceBelowMinBudget === null ||
        priceBetweenBudget === null ||
        priceAboveMaxBudget === null
      ) {
        showInvalidValueError();
        return;
      }
      const payload = {
        leadServicePlusPriceType,
        pricePerAreaBelowMinBudget: priceBelowMinBudget,
        pricePerAreaBetweenBudget: priceBetweenBudget,
        pricePerAreaAboveMaxBudget: priceAboveMaxBudget
      };
      const response = await updatePricesPerAreaForAgent(id, payload);
      showSuccess(response.data.message);
      updateSavedValues(values, payload);
      handleEditMode({ ...editMode, [actionsFor]: false });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingPricesPerArea(false);
    }
  };
  const onSubmitBudgetAreaQueryBounds = async (values) => {
    try {
      setIsSubmittingBudgetAreaOfQueryBounds(true);
      const { budgetAreaOfQueryBounds } = values;
      const response = await updateBudgetAreaOfQueryBoundsForAgent(id, {
        budgetAreaOfQueryBounds
      });
      showSuccess(response.data.message);
      handleEditMode({ ...editMode, [actionsFor]: false });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingBudgetAreaOfQueryBounds(false);
    }
  };
  const onSubmitLeadserviceIndividualNote = async (values, { forReset }) => {
    try {
      !forReset
        ? setIsSubmittingIndividualNote({
            ...isSubmittingIndividualNote,
            forAdd: true
          })
        : setIsSubmittingIndividualNote({
            ...isSubmittingIndividualNote,
            forReset: true
          });
      const note = forReset ? "" : values.leadServiceIndividualNote;
      const response = await updateLeadserviceIndividualNoteForAgent(id, {
        leadServiceIndividualNote: note
      });
      afterSubmit({
        values,
        forReset,
        msg: response.data.message,
        field: { prop: "leadServiceIndividualNote", val: note }
      });
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmittingIndividualNote({ forAdd: false, forReset: false });
    }
  };

  const isSubmittingMap = {
    [FOR_COMPANY]: isSubmittingCompany,
    [FOR_PRODUCT]: isSubmittingProduct,
    [FOR_PRICE]: isSubmittingPrice,
    [FOR_FIXED_PRICE]: isSubmittingFixedPrice,
    [FOR_UNIT]: isSubmittingUnit,
    [FOR_BUDGET_QUERY]: isSubmittingBudgetQueryLimits,
    [FOR_PRICES_PER_AREA]: isSubmittingPricesPerArea,
    [FOR_BUDGET_QUERY_BOUNDS]: isSubmittingBudgetAreaOfQueryBounds,
    [FOR_INDIVIDUAL_NOTE]: isSubmittingIndividualNote
  };
  const submitClickHandlers = {
    [FOR_COMPANY]: onSubmitCompanyName,
    [FOR_PRODUCT]: onSubmitProductName,
    [FOR_PRICE]: onSubmitPrice,
    [FOR_FIXED_PRICE]: onSubmitFixedPrice,
    [FOR_UNIT]: onSubmitUnit,
    [FOR_BUDGET_QUERY]: onSubmitBudgetQueryLimits,
    [FOR_PRICES_PER_AREA]: onSubmitPricesPerArea,
    [FOR_BUDGET_QUERY_BOUNDS]: onSubmitBudgetAreaQueryBounds,
    [FOR_INDIVIDUAL_NOTE]: onSubmitLeadserviceIndividualNote
  };
  const editClickHandlers = {
    [FOR_COMPANY]: () =>
      setInitialCompanyName(watch("companyNameForLeadAgent")),
    [FOR_PRODUCT]: () => setInitialProductName(watch("leadServiceProductName")),
    [FOR_PRICE]: () =>
      setInitialPrice(watch("pricePerInvoiceUnitForLeadAgent")),
    [FOR_FIXED_PRICE]: () =>
      setFixedInitialPrice(watch("fixedPricePerInvoiceUnitForLeadAgent")),
    [FOR_UNIT]: () => setInitialUnit(watch("nameOfAccountingUnitForLeadAgent")),
    [FOR_BUDGET_QUERY]: () => {
      setInitialMinBudgetQuery(watch("minBudgetQuery"));
      setInitialMaxBudgetQuery(watch("maxBudgetQuery"));
    },
    [FOR_PRICES_PER_AREA]: () => {
      setInitialPriceBelowMinBudget(watch("pricePerAreaBelowMinBudget"));
      setInitialPriceBetweenBudget(watch("pricePerAreaBetweenBudget"));
      setInitialPriceAboveMaxBudget(watch("pricePerAreaAboveMaxBudget"));
    },
    [FOR_BUDGET_QUERY_BOUNDS]: () =>
      setInitialBudgetAreaOfQueryBounds(watch("budgetAreaOfQueryBounds")),
    [FOR_INDIVIDUAL_NOTE]: () =>
      setInitialIndividualNote(watch("leadServiceIndividualNote"))
  };
  const cancelEditHandlers = {
    [FOR_COMPANY]: (_resetValues) => {
      _resetValues.companyNameForLeadAgent = initialCompanyName;
    },
    [FOR_PRODUCT]: (_resetValues) => {
      _resetValues.leadServiceProductName = initialProductName;
    },
    [FOR_PRICE]: (_resetValues) => {
      _resetValues.pricePerInvoiceUnitForLeadAgent = initialPrice;
    },
    [FOR_FIXED_PRICE]: (_resetValues) => {
      _resetValues.fixedPricePerInvoiceUnitForLeadAgent = initialFixedPrice;
    },
    [FOR_UNIT]: (_resetValues) => {
      _resetValues.nameOfAccountingUnitForLeadAgent = initialUnit;
    },
    [FOR_BUDGET_QUERY]: (_resetValues) => {
      _resetValues.minBudgetQuery = initialMinBudgetQuery;
      _resetValues.maxBudgetQuery = initialMaxBudgetQuery;
    },
    [FOR_PRICES_PER_AREA]: (_resetValues) => {
      _resetValues.pricePerAreaBelowMinBudget = initialPriceBelowMinBudget;
      _resetValues.pricePerAreaBetweenBudget = initialPriceBetweenBudget;
      _resetValues.pricePerAreaAboveMaxBudget = initialPriceAboveMaxBudget;
    },
    [FOR_BUDGET_QUERY_BOUNDS]: (_resetValues) => {
      _resetValues.budgetAreaOfQueryBounds = initialBudgetAreaOfQueryBounds;
    },
    [FOR_INDIVIDUAL_NOTE]: (_resetValues) => {
      _resetValues.leadServiceIndividualNote = initialIndividualNote;
    }
  };
  const deleteCheckMap = {
    [FOR_COMPANY]: () => funnel?.companyNameForLeadAgent,
    [FOR_PRODUCT]: () => funnel?.leadServiceProductName,
    [FOR_PRICE]: () => funnel?.pricePerInvoiceUnitForLeadAgent,
    [FOR_FIXED_PRICE]: () => funnel?.fixedPricePerInvoiceUnitForLeadAgent,
    [FOR_UNIT]: () => funnel?.nameOfAccountingUnitForLeadAgent,
    [FOR_BUDGET_QUERY]: () => funnel?.minBudgetQuery && funnel?.maxBudgetQuery,
    [FOR_PRICES_PER_AREA]: () =>
      funnel?.pricePerAreaBelowMinBudget &&
      funnel?.pricePerAreaBetweenBudget &&
      funnel?.pricePerAreaAboveMaxBudget,
    [FOR_INDIVIDUAL_NOTE]: () => funnel?.leadServiceIndividualNote
  };
  const submitHandlersWithReset = new Set([
    FOR_COMPANY,
    FOR_PRODUCT,
    FOR_UNIT,
    FOR_INDIVIDUAL_NOTE
  ]);

  const toggleEditMode = () =>
    handleEditMode({ ...editMode, [actionsFor]: !editMode[actionsFor] });

  const handleEditClick = () => {
    const handler = editClickHandlers[actionsFor];
    if (handler) {
      handler();
    }
    toggleEditMode();
  };

  const handleCancelEdit = () => {
    const resetValues = { ...getValues() };
    const handler = cancelEditHandlers[actionsFor];
    if (handler) {
      handler(resetValues);
    }
    reset(resetValues);
    toggleEditMode();
  };

  const handleSubmitClick = ({ forReset }) => {
    const handler = submitClickHandlers[actionsFor];
    if (!handler) return; // handling unknown actions
    const submit = handleSubmit(handler);
    if (submitHandlersWithReset.has(actionsFor)) {
      submit({ forReset });
    } else {
      submit();
    }
  };

  const checkIsSubmitting = ({ forReset }) => {
    const submittingEntry = isSubmittingMap[actionsFor];
    if (submitHandlersWithReset.has(actionsFor)) {
      return forReset ? submittingEntry.forReset : submittingEntry.forAdd;
    }
    return submittingEntry ?? false;
  };

  const checkIsDeleteDisabled = () => {
    const checkIsDisabled = deleteCheckMap[actionsFor];
    return !checkIsDisabled?.(); // if checkFn is undefined or returns false then return true
  };

  return (
    <Col>
      <div className="d-flex align-items-center">
        <Collapse dimension="width" in={editMode[actionsFor]}>
          <div>
            <div className="d-flex align-items-center">
              <LeadServiceActionButton
                faIcon={faCheck}
                isDisabled={isBtnDisabled}
                btnVariant="outline-success"
                tooltipMsg={t("common.save")}
                isLoading={checkIsSubmitting({ forReset: false })}
                clickHandler={() => handleSubmitClick({ forReset: false })}
              />
              <LeadServiceActionButton
                btnStyles="ms-2"
                faIcon={faXmark}
                btnVariant="outline-danger"
                tooltipMsg={t("common.cancel")}
                clickHandler={() => handleCancelEdit(actionsFor)}
              />
            </div>
          </div>
        </Collapse>
        <Collapse dimension="width" in={!editMode[actionsFor]}>
          <div>
            <div className="d-flex align-items-center">
              <LeadServiceActionButton
                faIcon={faEdit}
                btnVariant="outline-primary"
                tooltipMsg={t("common.edit")}
                clickHandler={() => handleEditClick(actionsFor)}
              />
              {!hideDelete && (
                <LeadServiceActionButton
                  btnStyles="ms-2"
                  faIcon={faTrashAlt}
                  tooltipMsg={t("delete")}
                  btnVariant="outline-danger"
                  isLoading={checkIsSubmitting({ forReset: true })}
                  clickHandler={() => handleSubmitClick({ forReset: true })}
                  isDisabled={checkIsDeleteDisabled()}
                />
              )}
            </div>
          </div>
        </Collapse>
      </div>
    </Col>
  );
};
export default LeadServiceActionsSection;
